import { useMemo } from 'react'
import { ApiVaultData, BaultPosition, FarmPosition, IslandPosition } from 'types/portfolio'
import { formatNumber } from 'utils/formatCurrencyAmount'
import { calculateV2TokenAmounts } from 'utils/portfolio/calculateV2TokenAmounts'
import { multiplyBigIntByNumber } from 'utils/portfolio/multiplyBigIntByNumber'
import { Address, formatEther, formatUnits } from 'viem'

import { useGetCharmUnderlyingAmounts } from './useGetCharmUnderlyingAmounts'
import { useIslandUnderlyingBalances } from './useIslandUnderlyingBalances'
import { useV2PoolsData } from './useV2PoolsData'

interface UnderlyingBalances {
  token0AmountFormatted: string
  token1AmountFormatted: string
  token0Amount: string
  token1Amount: string
}

function safeBigInt(value: string | number): bigint {
  if (typeof value === 'number') {
    return BigInt(Math.trunc(value))
  }
  if (value.includes('.')) {
    return BigInt(Math.trunc(parseFloat(value)))
  }
  return BigInt(value)
}

export function useGetUnderlyingTokenBalances(
  vaults: ApiVaultData[],
  islandPositions: IslandPosition[],
  baultPositions: BaultPosition[],
  farmPositions: FarmPosition[],
  options?: { enabled?: boolean }
) {
  const v2Positions = useMemo(() => {
    return islandPositions.filter((position) => position.vault.provider === 'v2')
  }, [islandPositions])
  const charmPositions = useMemo(() => {
    return islandPositions.filter((position) => position.vault.provider === 'charm')
  }, [islandPositions])
  const kodiakPositions = useMemo(() => {
    return islandPositions.filter((position) => position.vault.provider === 'kodiak')
  }, [islandPositions])

  const vaultsToFetch = useMemo(() => {
    const fromIslandPositions = kodiakPositions.map((position) => position.vault.id as Address)
    const fromBaultPositions = baultPositions.map((position) => position.vault.id as Address)
    const fromFarmPositions = farmPositions
      .filter((fp) => fp.vault.provider === 'kodiak')
      .map((fp) => fp.vault.id as Address)

    return [...new Set([...fromIslandPositions, ...fromBaultPositions, ...fromFarmPositions])]
  }, [kodiakPositions, baultPositions, farmPositions])

  // Collect v2 pool IDs from vaults
  const v2PoolIds = useMemo(() => {
    return v2Positions.map((position) => position.vault.pool!.id)
  }, [v2Positions])

  const charmVaultIds = useMemo(() => {
    const fromIslandPositions = charmPositions.map((position) => position.vault.id as Address)
    const fromFarmPositions = farmPositions
      .filter((fp) => fp.vault.provider === 'charm')
      .map((fp) => fp.vault.id as Address)

    return [...new Set([...fromIslandPositions, ...fromFarmPositions])]
  }, [charmPositions, farmPositions])

  const { data: islandUnderlyingData, isLoading: isLoadingIslandUnderlyingBalances } = useIslandUnderlyingBalances(
    vaultsToFetch,
    { enabled: options?.enabled && vaultsToFetch.length > 0 }
  )

  // Fetch v2 pool data from subgraph
  const { data: v2PoolsData, isLoading: isLoadingV2Pools } = useV2PoolsData(v2PoolIds, {
    enabled: options?.enabled && v2PoolIds.length > 0,
  })

  const charmUnderlyingAmounts = useGetCharmUnderlyingAmounts(charmVaultIds)

  // Calculate underlying balances for kodiak v3 positions only
  const islandUnderlyingBalances = useMemo(() => {
    if (isLoadingIslandUnderlyingBalances || !islandUnderlyingData) return {}

    const result: Record<string, UnderlyingBalances> = {}

    islandPositions.forEach((islandPosition) => {
      // Only process Kodiak v3 positions
      if (islandPosition.vault.provider !== 'kodiak') return

      const vaultKey = islandPosition.vault.id

      // Initialize with default values
      result[vaultKey] = {
        token0AmountFormatted: '0',
        token1AmountFormatted: '0',
        token0Amount: '0',
        token1Amount: '0',
      }

      const islandData = islandUnderlyingData?.[islandPosition.vault.id as Address]
      if (!islandData) {
        return
      }

      const { token0Amount: totalToken0, token1Amount: totalToken1, totalSupply: islandTotalSupply } = islandData

      if (totalToken0 === '0' && totalToken1 === '0' && islandTotalSupply === '0') {
        result[vaultKey] = {
          token0AmountFormatted: '0',
          token1AmountFormatted: '0',
          token0Amount: '0',
          token1Amount: '0',
        }
        return
      }

      // Calculate underlying for Island positions
      const userIslandBalance = safeBigInt(islandPosition.balance)
      const islandTotalSupplyBN = safeBigInt(islandTotalSupply)
      const totalToken0BN = safeBigInt(totalToken0)
      const totalToken1BN = safeBigInt(totalToken1)
      if (islandTotalSupplyBN === BigInt(0)) {
        return
      }

      // Formula: userUnderlying = (userBalance * totalUnderlying) / totalSupply
      const userToken0 = (userIslandBalance * totalToken0BN) / islandTotalSupplyBN
      const userToken1 = (userIslandBalance * totalToken1BN) / islandTotalSupplyBN

      const token0Formatted = formatUnits(userToken0, islandPosition.vault.token0?.decimals || 0)
      const token1Formatted = formatUnits(userToken1, islandPosition.vault.token1?.decimals || 0)

      result[vaultKey] = {
        token0AmountFormatted: formatNumber(parseFloat(token0Formatted)) || '0',
        token1AmountFormatted: formatNumber(parseFloat(token1Formatted)) || '0',
        token0Amount: userToken0.toString(),
        token1Amount: userToken1.toString(),
      }
    })

    return result
  }, [islandPositions, islandUnderlyingData])

  // Calculate underlying balances for each vault based on position type
  const baultUnderlyingBalances = useMemo(() => {
    if (isLoadingIslandUnderlyingBalances) return {}
    const result: Record<string, UnderlyingBalances> = {}
    baultPositions.forEach((baultPosition) => {
      const vaultKey = baultPosition.vault.id

      // Initialize with default values
      result[vaultKey] = {
        token0AmountFormatted: '0',
        token1AmountFormatted: '0',
        token0Amount: '0',
        token1Amount: '0',
      }
      const islandData = islandUnderlyingData?.[baultPosition.vault.id as Address]
      if (!islandData) {
        return {
          token0AmountFormatted: '0',
          token1AmountFormatted: '0',
          token0Amount: '0',
          token1Amount: '0',
        }
      }

      const { token0Amount: totalToken0, token1Amount: totalToken1, totalSupply: islandTotalSupply } = islandData

      if (totalToken0 === '0' && totalToken1 === '0' && islandTotalSupply === '0') {
        result[vaultKey] = {
          token0AmountFormatted: '0',
          token1AmountFormatted: '0',
          token0Amount: '0',
          token1Amount: '0',
        }
        return
      }

      // Calculate underlying for Bault positions
      const userIslandBalance = safeBigInt(baultPosition.balance)
      const islandTotalSupplyBN = safeBigInt(islandTotalSupply)
      const totalToken0BN = safeBigInt(totalToken0)
      const totalToken1BN = safeBigInt(totalToken1)
      if (islandTotalSupplyBN === BigInt(0)) {
        return
      }

      // Formula: userUnderlying = (userBalance * baultPriceInIslandTerms * totalUnderlying) / totalSupply
      const userToken0 = (userIslandBalance * totalToken0BN) / islandTotalSupplyBN
      const userToken1 = (userIslandBalance * totalToken1BN) / islandTotalSupplyBN

      const token0Formatted = formatUnits(
        multiplyBigIntByNumber(userToken0, Number(baultPosition.bault.priceVault)),
        baultPosition.vault.token0?.decimals || 0
      )
      const token1Formatted = formatUnits(
        multiplyBigIntByNumber(userToken1, Number(baultPosition.bault.priceVault)),
        baultPosition.vault.token1?.decimals || 0
      )

      result[vaultKey] = {
        token0AmountFormatted: formatNumber(parseFloat(token0Formatted)) || '0',
        token1AmountFormatted: formatNumber(parseFloat(token1Formatted)) || '0',
        token0Amount: userToken0.toString(),
        token1Amount: userToken1.toString(),
      }
    })

    return result
  }, [vaults, islandUnderlyingData, isLoadingIslandUnderlyingBalances])

  // Calculate underlying balances for staked v2 positions
  const v2UnderlyingBalances = useMemo(() => {
    if (!v2PoolsData) {
      return {}
    }

    const result: Record<string, UnderlyingBalances> = {}

    // Process v2 vaults that have farm positions
    vaults.forEach((vault) => {
      // Only process v2 vaults
      if (vault.provider !== 'v2' || !vault.pool?.id) return

      const vaultKey = vault.id
      const poolId = vault.pool.id.toLowerCase()
      const poolData = v2PoolsData[poolId]

      // Initialize with default values
      result[vaultKey] = {
        token0AmountFormatted: '0',
        token1AmountFormatted: '0',
        token0Amount: '0',
        token1Amount: '0',
      }

      if (!poolData) {
        console.warn(
          '🔴 v2UnderlyingBalances: No pool data found for v2 vault:',
          vaultKey,
          poolId,
          'Available pools:',
          Object.keys(v2PoolsData)
        )
        return
      }

      // For v2 positions, check both farm positions AND island positions
      // First try to find a farm position (staked)
      const farmPosition = farmPositions.find((fp) => fp.vault.id === vault.id)

      // If no farm position, check island positions (wallet held)
      const islandPosition = islandPositions.find((ip) => ip.vault.id === vault.id)

      // Use staked amount from farm OR balance from island position
      let userAmount = '0'
      if (farmPosition && farmPosition.stakedAmount && farmPosition.stakedAmount !== '0') {
        userAmount = farmPosition.stakedAmount
      } else if (islandPosition && islandPosition.balance && islandPosition.balance !== '0') {
        userAmount = islandPosition.balance
      } else {
        return
      }

      const { reserve0, reserve1, totalSupply } = poolData

      if (totalSupply === '0') {
        return
      }

      // Use the v2 calculation utility with user amount (either staked or wallet balance)
      const { token0Amount, token1Amount } = calculateV2TokenAmounts(
        formatEther(safeBigInt(userAmount)),
        totalSupply,
        reserve0,
        reserve1
      )

      if (parseFloat(token0Amount) === 0 && parseFloat(token1Amount) === 0) {
        return
      }

      // The calculated amounts are already in token units, just need formatting
      const token0Formatted = formatNumber(parseFloat(token0Amount)) || '0'
      const token1Formatted = formatNumber(parseFloat(token1Amount)) || '0'

      result[vaultKey] = {
        token0AmountFormatted: token0Formatted,
        token1AmountFormatted: token1Formatted,
        token0Amount,
        token1Amount,
      }
    })

    return result
  }, [vaults, farmPositions, islandPositions, v2PoolsData])

  const farmUnderlyingBalances = useMemo(() => {
    const result: Record<string, UnderlyingBalances> = {}

    farmPositions.forEach((farmPosition) => {
      const farmKey = farmPosition.farm.id
      const vaultKey = farmPosition.vault.id
      const vaultProvider = farmPosition.vault.provider

      // Initialize with default values
      result[farmKey] = {
        token0AmountFormatted: '0',
        token1AmountFormatted: '0',
        token0Amount: '0',
        token1Amount: '0',
      }

      let totalToken0 = '0'
      let totalToken1 = '0'
      let totalSupply = '0'

      if (vaultProvider === 'charm') {
        const charmData = charmUnderlyingAmounts?.[vaultKey as Address]
        if (!charmData) return

        totalToken0 = charmData.token0Amount
        totalToken1 = charmData.token1Amount
        totalSupply = charmData.totalSupply
      } else if (vaultProvider === 'kodiak') {
        const islandData = islandUnderlyingData?.[vaultKey as Address]
        if (!islandData) return

        totalToken0 = islandData.token0Amount
        totalToken1 = islandData.token1Amount
        totalSupply = islandData.totalSupply
      } else {
        return
      }

      if (totalToken0 === '0' && totalToken1 === '0' && totalSupply === '0') {
        return
      }

      // Calculate underlying for Farm positions using stakedAmount (which is LP tokens)
      const userStakedBalance = safeBigInt(farmPosition.stakedAmount)
      const totalSupplyBN = safeBigInt(totalSupply)
      const totalToken0BN = safeBigInt(totalToken0)
      const totalToken1BN = safeBigInt(totalToken1)

      if (totalSupplyBN === BigInt(0)) {
        return
      }

      // Formula: userUnderlying = (userStakedAmount * totalUnderlying) / totalSupply
      const userToken0 = (userStakedBalance * totalToken0BN) / totalSupplyBN
      const userToken1 = (userStakedBalance * totalToken1BN) / totalSupplyBN

      const token0Formatted = formatUnits(userToken0, farmPosition.vault.token0?.decimals || 0)
      const token1Formatted = formatUnits(userToken1, farmPosition.vault.token1?.decimals || 0)

      result[farmKey] = {
        token0AmountFormatted: formatNumber(parseFloat(token0Formatted)) || '0',
        token1AmountFormatted: formatNumber(parseFloat(token1Formatted)) || '0',
        token0Amount: userToken0.toString(),
        token1Amount: userToken1.toString(),
      }
    })

    return result
  }, [farmPositions, islandUnderlyingData, charmUnderlyingAmounts])

  // Calculate underlying balances for charm positions
  const charmUnderlyingBalances = useMemo(() => {
    if (!charmUnderlyingAmounts || Object.keys(charmUnderlyingAmounts).length === 0) return {}

    const result: Record<string, UnderlyingBalances> = {}

    charmPositions.forEach((charmPosition) => {
      const vaultKey = charmPosition.vault.id

      // Initialize with default values
      result[vaultKey] = {
        token0AmountFormatted: '0',
        token1AmountFormatted: '0',
        token0Amount: '0',
        token1Amount: '0',
      }

      const charmData = charmUnderlyingAmounts[charmPosition.vault.id as Address]
      if (!charmData) {
        return
      }

      const { token0Amount: totalToken0, token1Amount: totalToken1, totalSupply: charmTotalSupply } = charmData

      if (totalToken0 === '0' && totalToken1 === '0' && charmTotalSupply === '0') {
        return
      }

      // Calculate user's share based on their balance relative to total supply
      const userBalance = safeBigInt(charmPosition.balance)
      const totalSupplyBN = safeBigInt(charmTotalSupply)
      const totalToken0BN = safeBigInt(totalToken0)
      const totalToken1BN = safeBigInt(totalToken1)

      if (totalSupplyBN === BigInt(0)) {
        return
      }

      // Calculate user's proportional share
      const userToken0 = (userBalance * totalToken0BN) / totalSupplyBN
      const userToken1 = (userBalance * totalToken1BN) / totalSupplyBN

      const token0Formatted = formatUnits(userToken0, charmPosition.vault.token0?.decimals || 0)
      const token1Formatted = formatUnits(userToken1, charmPosition.vault.token1?.decimals || 0)

      result[vaultKey] = {
        token0AmountFormatted: formatNumber(parseFloat(token0Formatted)) || '0',
        token1AmountFormatted: formatNumber(parseFloat(token1Formatted)) || '0',
        token0Amount: userToken0.toString(),
        token1Amount: userToken1.toString(),
      }
    })

    return result
  }, [charmPositions, charmUnderlyingAmounts])

  return {
    data: {
      islandUnderlyingBalances,
      baultUnderlyingBalances,
      farmUnderlyingBalances,
      v2UnderlyingBalances,
      charmUnderlyingBalances,
    },
    isLoading: isLoadingIslandUnderlyingBalances || isLoadingV2Pools,
  }
}
